import { useState } from 'react';
import { adLibraryUrl, creativeUrl, type Ad } from './api';
import { categoryLabel } from './categories';
import { estimateSpend, formatSpend } from './spesa';

const PLATFORM_LABEL: Record<string, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  audience_network: 'Audience Network',
  messenger: 'Messenger',
};

const GENDER_LABEL: Record<string, string> = {
  Women: 'donne',
  Men: 'uomini',
  All: 'tutti',
};

const dateFmt = new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'short' });
const dateFmtYear = new Intl.DateTimeFormat('it-IT', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});
const numFmt = new Intl.NumberFormat('it-IT');

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const fmt = d.getFullYear() === new Date().getFullYear() ? dateFmt : dateFmtYear;
  return fmt.format(d);
}

function isNew(ad: Ad): boolean {
  return Date.now() - new Date(ad.firstSeenAt).getTime() < 48 * 3600 * 1000;
}

/** Le varianti creative arrivano unite con "\n---\n" dal backend. */
function splitVariants(joined: string | null): string[] {
  if (!joined) return [];
  return joined
    .split('\n---\n')
    .map((v) => v.trim())
    .filter(Boolean);
}

/** "Italia,-Sicilia" → "Italia (escl. Sicilia)"; oltre 3 nomi compatta in "+n". */
function formatLocations(csv: string | null): string | null {
  if (!csv) return null;
  const all = csv.split(',').map((s) => s.trim()).filter(Boolean);
  const included = all.filter((s) => !s.startsWith('-'));
  const excluded = all.filter((s) => s.startsWith('-')).map((s) => s.slice(1));
  let out = included.slice(0, 3).join(', ');
  if (included.length > 3) out += ` +${included.length - 3}`;
  if (excluded.length > 0) out += ` (escl. ${excluded.slice(0, 2).join(', ')})`;
  return out || null;
}

/** Riga di targeting EU: "reach 1.234 · ≈ 40–90 € · donne · 18-64 · Italia". */
function euLine(ad: Ad): string | null {
  const parts: string[] = [];
  if (ad.euTotalReach != null) parts.push(`reach ${numFmt.format(ad.euTotalReach)}`);
  const est = estimateSpend(ad.euTotalReach, ad.category);
  const spend = formatSpend(est);
  if (spend) {
    // La stima è CUMULATIVA sulla vita dell'annuncio; il /giorno aiuta a
    // confrontare annunci con durate diverse
    const days = ad.deliveryDays != null && ad.deliveryDays > 0 ? ad.deliveryDays : null;
    const perDay = est && days ? Math.round((est.min + est.max) / 2 / days) : null;
    parts.push(`${spend} totali${perDay ? ` (~${perDay} €/gg)` : ''}`);
  }
  const gender = ad.targetGender ? GENDER_LABEL[ad.targetGender] ?? ad.targetGender : null;
  if (gender && gender !== 'tutti') parts.push(gender);
  if (ad.targetAges) parts.push(`${ad.targetAges} anni`);
  const loc = formatLocations(ad.targetLocations);
  if (loc) parts.push(loc);
  return parts.length ? parts.join(' · ') : null;
}

interface Props {
  ad: Ad;
  onFilterPage: (pageId: string, pageName: string) => void;
}

export default function AdItem({ ad, onFilterPage }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  // La cattura è lazy server-side: 404 = nessuna immagine → si nasconde
  const [imgFailed, setImgFailed] = useState(false);

  const bodies = splitVariants(ad.adCreativeBodies);
  const titles = splitVariants(ad.adCreativeLinkTitles);
  const active = !ad.adDeliveryStopTime;
  const start = formatDate(ad.adDeliveryStartTime);
  const stop = formatDate(ad.adDeliveryStopTime);
  const platforms = (ad.publisherPlatforms ?? '')
    .split(',')
    .map((p) => PLATFORM_LABEL[p.trim()] ?? p.trim())
    .filter(Boolean);
  const targeting = euLine(ad);
  const audience = [ad.targetGender && ad.targetGender !== 'All' ? GENDER_LABEL[ad.targetGender] ?? ad.targetGender : null, ad.targetAges].filter(Boolean).join(' ') || null;
  const brandInitial = (ad.pageName ?? 'A').trim().charAt(0).toUpperCase();

  const mainBody = bodies[0] ?? null;
  const extraVariants = bodies.length - 1;
  const longBody = (mainBody?.length ?? 0) > 340 || (mainBody?.split('\n').length ?? 0) > 6;
  const collapsible = longBody || extraVariants > 0;

  return (
    <article className="ad">
      <header className="ad-head">
        <div className="ad-brand">
          <span className={active ? 'ad-brand-state is-active' : 'ad-brand-state'} aria-hidden="true" />
          <span className="ad-avatar" aria-hidden="true">{brandInitial}</span>
          {ad.pageId ? (
            <button type="button" className="ad-page" onClick={() => onFilterPage(ad.pageId!, ad.pageName ?? ad.pageId!)} title="Mostra solo gli annunci di questa pagina">
              {ad.pageName ?? 'Pagina sconosciuta'}
            </button>
          ) : <span className="ad-page">{ad.pageName ?? 'Pagina sconosciuta'}</span>}
        </div>
        <span className={active ? 'status status-on' : 'status'}>
          {active ? 'Attivo' : stop ? `Fino al ${stop}` : 'Concluso'}
        </span>
      </header>

      <p className="ad-meta">
        {start && <span>dal {start}</span>}
        {ad.deliveryDays != null && ad.deliveryDays > 0 && (
          <span>{ad.deliveryDays}gg in onda</span>
        )}
        {platforms.length > 0 && <span>{platforms.join(' · ')}</span>}
        {ad.category && <span>{categoryLabel(ad.category)}</span>}
        {ad.variantsCount != null && ad.variantsCount > 1 && (
          <span>{ad.variantsCount} varianti</span>
        )}
        {isNew(ad) && <span className="badge-new">Nuovo in archivio</span>}
      </p>

      {!imgFailed && (
        <img
          className="ad-image"
          src={creativeUrl(ad.id)}
          alt={`Creative di ${ad.pageName ?? 'annuncio'}`}
          loading="lazy"
          onError={() => setImgFailed(true)}
        />
      )}

      {titles[0] && <p className="ad-title">{titles[0]}</p>}

      {mainBody ? (
        <>
          <p className={expanded || !longBody ? 'ad-body' : 'ad-body ad-body-clamp'}>
            {mainBody}
          </p>
          {expanded &&
            bodies.slice(1).map((v, i) => (
              <p key={i} className="ad-body ad-variant">
                {v}
              </p>
            ))}
          {collapsible && (
            <button
              type="button"
              className="link-btn"
              onClick={() => setExpanded((e) => !e)}
            >
              {expanded
                ? 'Mostra meno'
                : extraVariants > 0
                  ? `Mostra tutto (+${extraVariants} ${extraVariants === 1 ? 'variante' : 'varianti'})`
                  : 'Mostra tutto'}
            </button>
          )}
        </>
      ) : (
        <p className="ad-body ad-body-empty">
          Annuncio senza testo (solo immagine o video): aprilo nell'Ad Library per vedere la
          creative.
        </p>
      )}

      <footer className="ad-foot">
        <div className="ad-card-metrics">
          {ad.euTotalReach != null && <span><small>Reach</small><strong>{numFmt.format(ad.euTotalReach)}</strong></span>}
          {ad.deliveryDays != null && ad.deliveryDays > 0 && <span><small>Durata</small><strong>{ad.deliveryDays} giorni</strong></span>}
          {audience && <span><small>Pubblico</small><strong>{audience}</strong></span>}
        </div>
        {detailsOpen && <div className="ad-details"><p className="ad-meta">{start && <span>Dal {start}</span>}{platforms.length > 0 && <span>{platforms.join(' · ')}</span>}{ad.category && <span>{categoryLabel(ad.category)}</span>}{ad.variantsCount != null && ad.variantsCount > 1 && <span>{ad.variantsCount} varianti</span>}</p>{targeting && <p className="ad-eu">{targeting}</p>}</div>}
        <div className="ad-actions">
          <button type="button" className="link-btn" aria-expanded={detailsOpen} onClick={() => setDetailsOpen((value) => !value)}>{detailsOpen ? 'Chiudi dettagli' : 'Dettagli'}</button>
          <a href={adLibraryUrl(ad.metaAdArchiveId)} target="_blank" rel="noopener noreferrer">Ad Library ↗</a>
        </div>
      </footer>
    </article>
  );
}
